import React, { useCallback } from "react";
import { getAxios, postAxios, putAxios } from "../components/utils/axios";

/** @typedef {import("axios").AxiosInstance} AxiosInstance */
/** @typedef {import("axios").AxiosError} AxiosError */
/** @typedef {import("axios").AxiosResponse} AxiosResponse */

/**
 * @template T
 * @typedef {Object} UseAxiosResult
 * @property {boolean} isLoading - Whether a request is in progress
 * @property {AxiosError|null} error - The last error that occurred, if any
 * @property {(url: string) => Promise<T>} get - GET request function
 * @property {(url: string, data: any) => Promise<T>} post - POST request function
 * @property {(url: string, data: any) => Promise<T>} put - PUT request function
 */

/**
 * Custom hook for making Axios HTTP requests
 * @template T
 * @returns {UseAxiosResult<T>}
 */
const useAxiosHook = () => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState(null);

    const onLoad = useCallback(() => setIsLoading(true), []);
    const onFinish = useCallback(() => setIsLoading(false), []);

    /**
     * Generic request handler
     * @template T
     * @param {AxiosInstance['get']|AxiosInstance['post']|AxiosInstance['put']} method 
     * @param {string} url 
     * @param {any} [data] 
     * @returns {Promise<T>}
     */
    const load = useCallback(async (method, url, data = null) => {
        try {
            setError(null);
            onLoad();
            const response = await method(url, data);
            return response.data;
        } catch (e) {
            const axiosError = /** @type {AxiosError} */ (e);
            setError(axiosError);
            throw axiosError;
        } finally {
            onFinish();
        }
    }, [onFinish, onLoad]);

    const get = useCallback(async (url) => load(getAxios, url), [load]);
    const post = useCallback(async (url, data) => load(postAxios, url, data), [load]);
    const put = useCallback(async (url, data) => load(putAxios, url, data), [load]);

    return { isLoading, error, get, post, put };
};

export default useAxiosHook;
